import { useDescope } from "@descope/react-sdk";
import Delete from "@mui/icons-material/Delete";
import {
	Box, Button, FormControlLabel, Skeleton,
	Stack, Switch, Typography
} from "@mui/material";
import axios, { type AxiosError, type AxiosResponse } from "axios";
import { type ChangeEvent,useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import pages from "../../../constants/pages";
import prettifyDate from "../../../helpers/prettifyDate";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useAuthorization from "../../../hooks/useAuthorization";
import useToastAlert from "../../../hooks/useToastAlert";
import { resetNavigationState } from "../../../redux/navigationSlice";
import type { UserState } from "../../../redux/userSlice";
import {
	selectOrganizationSsoAndScim, selectUser, setEmail as setReduxUserEmail, setFirstName,
	setGroups, setLastName, setOrgId,
	setPermissions, setPhone, setRoles,
	setUserId
} from "../../../redux/userSlice";
import CoAppMultiSelect from "../../global/components/inputs/CoAppMultiSelect";
import CoAppTextField from "../../global/components/inputs/CoAppTextField";
import CoAppConfirmationModal from "../../global/components/modals/CoAppConfirmationModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";
import { CoAppActionButton, CoAppCancelTextButton, CoAppProfileAvatar } from "../../global/styled/global.styled";
import { UserProfileNameText } from "./styled/user-profile.styled";

interface RoleOption {
	id: string;
	name: string;
	[key: string]: unknown;
}

interface GroupOption {
	id: string;
	name: string;
	[key: string]: unknown;
}

interface EditableUserDetails {
	active: boolean;
	firstName: string;
	lastName: string;
	roles: string[];
	groups: string[];
}

interface UserDetailsState {
	onLoadDetails: EditableUserDetails;
	onEditDetails: EditableUserDetails;
}

interface UserApiResponse {
	active?: boolean;
	firstname?: string;
	lastname?: string;
	roles?: string[];
	groups?: Array<{ name: string }>;
	email?: string;
	lastHeartbeat?: string | null;
	createdAt?: string;
	isCoAppSupport?: boolean;
}

const initialUserDetails: UserDetailsState = {
	onLoadDetails: {
		active: false,
		firstName: "",
		lastName: "",
		roles: [],
		groups: []
	},
	onEditDetails: {
		active: false,
		firstName: "",
		lastName: "",
		roles: [],
		groups: []
	}
};
export default function UserProfile() {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const isAuthorized = useAuthorization(["MANAGE_USERS_AND_GROUPS"]);
	const isSsoAndScimEnabled = useSelector(selectOrganizationSsoAndScim) ?? false;
	const user = useSelector(selectUser) as UserState;
	const { id = "" } = useParams<{ id: string }>();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { logout } = useDescope();

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [isDisableDialogOpen, setIsDisableDialogOpen] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

	const [availableGroups, setAvailableGroups] = useState<GroupOption[]>([]);
	const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
	const [email, setEmail] = useState<string>("");
	const [isAuthorizedToEdit, setIsAuthorizedToEdit] = useState<boolean>(false);
	const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
	const [lastHeartbeat, setLastHeartbeat] = useState<string>("");
	const [userCreationDate, setUserCreationDate] = useState<string>("");
	const [userDetails, setUserDetails] = useState<UserDetailsState>(initialUserDetails);
	const [userUpdateSeed, setUserUpdateSeed] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(true);

	const getAvatarText = () => {
		if (!email) return null;
		if (userDetails.onLoadDetails.firstName && userDetails.onLoadDetails.lastName) {
			return userDetails.onLoadDetails.firstName[0].toUpperCase() + userDetails.onLoadDetails.lastName[0].toUpperCase();
		} else {
			return email[0].toUpperCase();
		}
	};

	/**
	 * User confirms account de-activation
	 * - Deactive user
	 * - If currently logged in user, log them out, redirect to login page.
	 * - If not currently logged in user, just de-activate user. 
	 */
	const handleDeactivateAccountClick = () => {
		const numericId = Number(id);
		if (!id) {
			return;
		}
		const userJSON = {
			active: userDetails.onLoadDetails.active ? false : true
		};
		axios.put(apiRoutes.setUserActive(id), userJSON, {
			headers: authHeader
		}).then(() => {
			if (user.userId === numericId) {
				logout();
				dispatch(setUserId(null));
				dispatch(setReduxUserEmail(""));
				dispatch(setFirstName(""));
				dispatch(setLastName(""));
				dispatch(setPhone(""));
				dispatch(setRoles([]));
				dispatch(setOrgId(null));
				dispatch(resetNavigationState());
				navigate(pages.login);
			} else {
				initUserProfile();
				handleToggleDisableDialog();
			}
		}).catch((err) => {
			console.log(err);
			handleUpdateUserAlert(messageLevels.ERROR);
		});
	};

	const handleDeleteAccount = () => {
		const numericId = Number(id);
		if (!id) {
			return;
		}
		axios.delete(apiRoutes.deleteUser(id), {
			headers: authHeader
		}).then(() => {
			if (user.userId === numericId) {
				logout();
				dispatch(setUserId(null));
				dispatch(setReduxUserEmail(""));
				dispatch(setFirstName(""));
				dispatch(setLastName(""));
				dispatch(setPhone(""));
				dispatch(setRoles([]));
				dispatch(setOrgId(null));
				dispatch(resetNavigationState());
				navigate(pages.login);
			} else {
				handleToggleDeleteDialog();
				navigate(pages.userManagement, { replace: true });
			}
		}).catch((err) => {
			console.log(err);
			handleToastAlert(messageLevels.ERROR, messages.USER_DELETION_ERROR_MSG);
		});
	};


	const handleSaveChangesClick = () => {
		if (!id) {
			return;
		}
		const userJSON: Record<string, unknown> = {};
		const { onLoadDetails, onEditDetails } = userDetails;

		if (onLoadDetails.firstName !== onEditDetails.firstName) {
			userJSON.firstName = onEditDetails.firstName;
		}
		if (onLoadDetails.lastName !== onEditDetails.lastName) {
			userJSON.lastName = onEditDetails.lastName;
		}
		if (JSON.stringify(onLoadDetails.roles) !== JSON.stringify(onEditDetails.roles)) {
			userJSON.roles = onEditDetails.roles;
		}
		if (JSON.stringify(onLoadDetails.groups) !== JSON.stringify(onEditDetails.groups)) {
			userJSON.groups = onEditDetails.groups;
		}

		if (Object.keys(userJSON).length > 0) {
			axios.put(apiRoutes.updateUser + "/" + id, userJSON, {
				headers: authHeader
			}).then((res: AxiosResponse<any>) => {
				const numericId = Number(id);
				if (user.userId === numericId) {
					dispatch(setFirstName(res.data.firstname));
					dispatch(setLastName(res.data.lastname));
					dispatch(setGroups(res.data.groups));
					dispatch(setRoles(res.data.roles));
					dispatch(setPermissions(res.data.permissions));
				}
				setIsEditing(false);
			}).catch((err: AxiosError) => {
				handleUpdateUserAlert(messageLevels.ERROR);
				setUserDetails({ ...userDetails, onEditDetails: userDetails.onLoadDetails });
				console.log(err);
				return null;
			});
		}
	};

	const handleUpdateUserAlert = (level: string) => {
		setUserUpdateSeed(Math.random());
		setIsEditing(false);
		handleToastAlert(level, level === "success" ? messages.USER_UPDATE_SUCCESS_MSG : messages.USER_UPDATE_ERROR_MSG);
	};

	const handleToggleDisableDialog = () => {
		setIsDisableDialogOpen(!isDisableDialogOpen);
	};

	const handleToggleDeleteDialog = () => {
		setIsDeleteDialogOpen(!isDeleteDialogOpen);
	};

	const handleMultiselectChange = (_e: unknown, value: string[], field: "roles" | "groups") => {
		const newDetails: UserDetailsState = {
			...userDetails,
			onEditDetails: { ...userDetails.onEditDetails, [field]: value }
		};
		if (userDetailsHaveChanged(newDetails)) {
			setIsEditing(true);
		} else {
			setIsEditing(false);
		}
		setUserDetails(newDetails);
	};

	const editableKeys: Array<keyof EditableUserDetails> = ["firstName", "lastName"];

	const handleValueChange = (e: ChangeEvent<HTMLInputElement>, field?: keyof EditableUserDetails) => {
		const targetField = (e.target.id !== undefined && e.target.id !== "" ? e.target.id : field) as keyof EditableUserDetails | undefined;
		if (!targetField || !editableKeys.includes(targetField)) {
			return;
		}
		const newDetails: UserDetailsState = {
			...userDetails,
			onEditDetails: {
				...userDetails.onEditDetails,
				[targetField]: e.target.value
			}
		};
		if (userDetailsHaveChanged(newDetails)) {
			setIsEditing(true);
		} else {
			setIsEditing(false);
		}
		setUserDetails(newDetails);
	};

	const handleCancelButtonClick = () => {
		setUserDetails({ ...userDetails, onEditDetails: userDetails.onLoadDetails });
		setIsEditing(false);
	};

	const initUserProfile = async () => {
		try {
			const numericId = Number(id);
			const isSelf = user?.userId ? (user.userId === numericId ? 1 : 0) : 0;
			const response = await axios.get<UserApiResponse>(apiRoutes.getUser(isSelf, id, "", ""), { headers: authHeader });
			if (response.data) {
				const { data } = response;
				const details: UserDetailsState = {
					onLoadDetails: {
						active: data.active || false,
						firstName: data.firstname || "",
						lastName: data.lastname || "",
						roles: data.roles || [],
						groups: data.groups ? data.groups.map(g => g.name) : [],
					},
					onEditDetails: {
						active: data.active || false,
						firstName: data.firstname || "",
						lastName: data.lastname || "",
						roles: data.roles || [],
						groups: data.groups ? data.groups.map(g => g.name) : [],
					}
				};
				setUserDetails(details);
				setEmail(data.email || "");
				setLoading(false);
				setLastHeartbeat(data.lastHeartbeat === null || data.lastHeartbeat === undefined ? "Never" : prettifyDate(data.lastHeartbeat));
				setUserCreationDate(data.createdAt ? prettifyDate(data.createdAt) : "");
				setIsSuperAdmin(Boolean(data.isCoAppSupport));
				const isSuperAdminUser = Boolean(data.isCoAppSupport);

				if (isAuthorized && !isSuperAdminUser) {
					setIsAuthorizedToEdit(true);
				} else {
					setIsAuthorizedToEdit(false);
				}

				await initRoleAndGroupOptions();
			}
		} catch (err) {
			console.log(err);
		}
	};
	
	const initRoleAndGroupOptions = async () => {
		try {
			const [groupsResponse, rolesResponse] = await Promise.all([
				axios.get<GroupOption[]>(apiRoutes.getGroups, { 
					headers: authHeader,
				}),
				axios.get<RoleOption[]>(apiRoutes.getRoles, { 
					headers: authHeader,
				})
			]);
			
			if (groupsResponse.data) {
				setAvailableGroups(groupsResponse.data);
			}
			
			if (rolesResponse.data) {
				setAvailableRoles(rolesResponse.data);
			}
		} catch (err) {
			console.log(err);
		}
	};

	const userDetailsHaveChanged = (details: UserDetailsState): boolean => {
		const temp: UserDetailsState = {
			...details,
			onEditDetails: {
				...details.onEditDetails,
				roles: [...details.onEditDetails.roles].sort(),
				groups: [...details.onEditDetails.groups].sort()
			},
			onLoadDetails: {
				...details.onLoadDetails,
				roles: [...details.onLoadDetails.roles].sort(),
				groups: [...details.onLoadDetails.groups].sort()
			}
		};
		return JSON.stringify(temp.onLoadDetails) !== JSON.stringify(temp.onEditDetails);
	};

	useEffect(() => {
		initUserProfile();
	}, [id, userUpdateSeed]);

	return (
		<>
			<Stack sx={{ padding: "24px 32px" }} spacing={3}>
				{/* Header row: avatar + name + active toggle */}
				<Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
					{loading ? (
						<>
							<Skeleton variant="circular" width={52} height={52} />
							<Skeleton variant="rectangular" width={180} height={32} sx={{ borderRadius: "8px" }} />
						</>
					) : (
						<>
							<CoAppProfileAvatar h="52px" w="52px" fs="22px">
								{getAvatarText()}
							</CoAppProfileAvatar>
							<Box sx={{ minWidth: 0 }}>
								<UserProfileNameText>
									{userDetails.onLoadDetails.firstName && userDetails.onLoadDetails.lastName
										? `${userDetails.onLoadDetails.firstName} ${userDetails.onLoadDetails.lastName}`
										: null}
								</UserProfileNameText>
								{userCreationDate ? (
									<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.45)", mt: 0.25, fontSize: 12 }}>
										Created: {userCreationDate.toString().replace(",", " at ")}
									</Typography>
								) : null}
							</Box>
							<FormControlLabel
								sx={{ m: 0 }}
								labelPlacement="end"
								label={
									<Typography variant="body2" sx={{ fontWeight: 500, color: userDetails.onLoadDetails.active ? "rgba(13, 31, 48, 0.86)" : "rgba(13, 31, 48, 0.45)", ml: 0.5 }}>
										{userDetails.onLoadDetails.active ? "Active" : "Inactive"}
									</Typography>
								}
								control={
									<Switch
										checked={userDetails.onLoadDetails.active}
										onClick={handleToggleDisableDialog}
										disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isSuperAdmin)}
									/>
								}
							/>
						</>
					)}
				</Stack>

				{/* Form fields */}
				<Stack spacing={2} sx={{ width: "100%", maxWidth: 520 }}>
					<Stack direction={{ sm: "row", xs: "column" }} spacing={2} sx={{ width: "100%" }}>
						{loading ? (
							<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "8px", flex: 1 }} />
						) : (
							<CoAppTextField
								sx={{ flex: 1, minWidth: 0 }}
								id="firstName"
								label="First Name"
								onChange={handleValueChange}
								value={userDetails.onEditDetails.firstName}
								disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isSuperAdmin)}
							/>
						)}
						{loading ? (
							<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "8px", flex: 1 }} />
						) : (
							<CoAppTextField
								sx={{ flex: 1, minWidth: 0 }}
								id="lastName"
								label="Last Name"
								value={userDetails.onEditDetails.lastName}
								onChange={handleValueChange}
								disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isSuperAdmin)}
							/>
						)}
					</Stack>

					{loading ? (
						<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "8px", width: "100%" }} />
					) : (
						<CoAppTextField fullWidth disabled id="email" label="Email" value={email} />
					)}

					{loading ? (
						<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "8px", width: "100%" }} />
					) : (
						<CoAppMultiSelect
							disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isSuperAdmin)}
							label="Roles"
							options={availableRoles.map(r => r.name)}
							value={userDetails.onEditDetails.roles}
							onChange={(e, value) => handleMultiselectChange(e, value, "roles")}
						/>
					)}

					{loading ? (
						<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "8px", width: "100%" }} />
					) : (
						<CoAppMultiSelect
							disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isSuperAdmin)}
							label="Groups"
							options={availableGroups.map(g => g.name)}
							value={userDetails.onEditDetails.groups}
							onChange={(e, value) => handleMultiselectChange(e, value, "groups")}
						/>
					)}
				</Stack>

				{/* Footer: delete + save/cancel */}
				<Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", pt: 1 }}>
					{loading ? (
						<Skeleton variant="rectangular" height={36} width={140} sx={{ borderRadius: "8px" }} />
					) : (
						<Button
							variant="contained"
							startIcon={<Delete sx={{ fontSize: 16 }} />}
							onClick={handleToggleDeleteDialog}
							disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isSuperAdmin)}
							sx={{
								background: "#B42318",
								color: "#fff",
								fontWeight: 600,
								fontSize: 13,
								borderRadius: 2,
								px: 2,
								py: 0.875,
								boxShadow: "none",
								textTransform: "none",
								"&:hover": { background: "#921C14", boxShadow: "none" },
								"&:active": { background: "#7A1710", boxShadow: "none" }
							}}
						>
							Delete account
						</Button>
					)}
					<Stack direction="row" sx={{ gap: 1 }}>
						<CoAppCancelTextButton onClick={handleCancelButtonClick} sx={{ display: isEditing ? "flex" : "none" }}>Cancel</CoAppCancelTextButton>
						<CoAppActionButton onClick={handleSaveChangesClick} sx={{ display: isEditing ? "flex" : "none" }}>Save</CoAppActionButton>
					</Stack>
				</Stack>
			</Stack>
			<CoAppDestructiveConfirmationModal
				dialogOpen={isDisableDialogOpen && userDetails.onLoadDetails.active}
				dialogMessage={`Are you sure you want to disable ${email}? The user will no longer be able to log in once disabled.`}
				dialogTitle={`Disable ${email}`}
				confirmClickHandler={handleDeactivateAccountClick}
				cancelClickHandler={handleToggleDisableDialog}
				actionText="Disable"
			/>
			<CoAppConfirmationModal
				dialogOpen={isDisableDialogOpen && !userDetails.onLoadDetails.active}
				dialogMessage={`Are you sure you want to enable ${email}? The user will be able to log in once enabled.`}
				dialogTitle={`Enable ${email}`}
				confirmClickHandler={handleDeactivateAccountClick}
				cancelClickHandler={handleToggleDisableDialog}
				actionText="Enable"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={isDeleteDialogOpen}
				dialogMessage={`Are you sure you want to delete ${email}? The user will no longer exist in your organization once deleted.`}
				dialogTitle={`Delete ${email}`}
				confirmClickHandler={handleDeleteAccount}
				cancelClickHandler={handleToggleDeleteDialog}
				actionText="Delete"
			/>
		</>
	);
}
